import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, PackageCheck, Search, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { getAssignedCustomer } from '@/components/pos/posCustomer'
import { fetchRedeemablePackages } from '@/lib/api/packages'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import {
  filterRedeemablePackages,
  findRedeemableService,
} from '@/lib/pos/posPackageRedeem'
import { isFlexibleComboPackage } from '@/lib/pos/posFlexibleCombo'
import { posPackageTypeLabel } from '@/lib/pos/posPackage'
import { PosFlexibleComboPicker } from '@/components/pos/PosFlexibleComboPicker'
import { cn } from '@/lib/utils'

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {import('@/types/api').Sale | null} props.sale
 * @param {() => void} props.onClose
 * @param {(payload: { customer_package_id: number, service_id: number }) => Promise<boolean>} props.onRedeem
 * @param {(payload: { customer_package_id: number, service_ids: number[] }) => Promise<boolean>} [props.onRedeemBatch]
 * @param {boolean} props.submitting
 * @param {string} [props.error]
 */
export function PosPackageRedeemModal({
  open,
  sale,
  onClose,
  onRedeem,
  onRedeemBatch,
  submitting,
  error = '',
}) {
  const [query, setQuery] = useState('')
  const [packages, setPackages] = useState([])
  const [loading, setLoading] = useState(false)
  const [localError, setLocalError] = useState('')
  const [selectedPackageId, setSelectedPackageId] = useState(null)
  const [selectedServiceId, setSelectedServiceId] = useState(null)
  const [selectedFlexServiceIds, setSelectedFlexServiceIds] = useState([])

  const customer = getAssignedCustomer(sale)
  const selectedPackage = packages.find((pkg) => pkg.customer_package_id === selectedPackageId) ?? null
  const selectedService = findRedeemableService(selectedPackage, selectedServiceId)
  const isFlexibleCombo = isFlexibleComboPackage(selectedPackage)

  const visiblePackages = useMemo(
    () => filterRedeemablePackages(packages, query),
    [packages, query],
  )

  const loadPackages = useCallback(async () => {
    if (!customer?.id) {
      setPackages([])
      return
    }

    setLoading(true)
    setLocalError('')

    try {
      const items = await fetchRedeemablePackages(customer.id, sale?.id)
      setPackages(items)
    } catch (err) {
      setLocalError(apiErrorMessage(err, 'Unable to load package balances.'))
      setPackages([])
    } finally {
      setLoading(false)
    }
  }, [customer?.id, sale?.id])

  useEffect(() => {
    if (!open) {
      setQuery('')
      setSelectedPackageId(null)
      setSelectedServiceId(null)
      setSelectedFlexServiceIds([])
      setLocalError('')
      return
    }

    loadPackages()
  }, [loadPackages, open])

  const handleRedeem = useCallback(async () => {
    if (!selectedPackageId) return

    if (isFlexibleCombo) {
      if (selectedFlexServiceIds.length === 0 || !onRedeemBatch) return

      const redeemed = await onRedeemBatch({
        customer_package_id: selectedPackageId,
        service_ids: selectedFlexServiceIds,
      })

      if (redeemed) {
        onClose()
      }
      return
    }

    if (!selectedServiceId) return

    const redeemed = await onRedeem({
      customer_package_id: selectedPackageId,
      service_id: selectedServiceId,
    })

    if (redeemed) {
      onClose()
    }
  }, [
    isFlexibleCombo,
    onClose,
    onRedeem,
    onRedeemBatch,
    selectedFlexServiceIds,
    selectedPackageId,
    selectedServiceId,
  ])

  if (!open) return null

  const displayError = error || localError
  const needsCustomer = !customer

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={submitting ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pos-package-redeem-title"
        className="card-surface flex max-h-[min(82vh,680px)] w-full max-w-2xl flex-col overflow-hidden shadow-luxury-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-border/50 px-4 py-3">
          <div>
            <p id="pos-package-redeem-title" className="flex items-center gap-2 text-sm font-semibold">
              <PackageCheck className="h-4 w-4 text-primary" />
              Redeem package
            </p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Add covered services at AED 0 · F7 shortcut
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {needsCustomer ? (
          <div className="border-b border-amber-200/60 bg-amber-50 px-4 py-3 text-[11px] text-amber-900">
            Assign a customer with an active package before redeeming (F2 — customer search).
          </div>
        ) : (
          <div className="border-b border-border/40 bg-muted/20 px-4 py-2 text-[11px] text-muted-foreground">
            Redeeming for <span className="font-semibold text-foreground">{customer.name}</span>
          </div>
        )}

        <div className="grid min-h-0 flex-1 md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
          <div className="flex min-h-0 flex-col border-b border-border/40 md:border-b-0 md:border-r">
            <div className="border-b border-border/40 px-4 py-3">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search active packages…"
                  disabled={loading || submitting || needsCustomer}
                  className="w-full rounded-xl border border-input bg-background py-2 pl-9 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading balances…
                </div>
              ) : visiblePackages.length === 0 ? (
                <p className="px-2 py-8 text-center text-[11px] text-muted-foreground">
                  {needsCustomer
                    ? 'Customer required.'
                    : 'No redeemable package balances for this guest.'}
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {visiblePackages.map((pkg) => {
                    const selected = pkg.customer_package_id === selectedPackageId

                    return (
                      <li key={pkg.customer_package_id}>
                        <button
                          type="button"
                          disabled={submitting}
                          onClick={() => {
                            setSelectedPackageId(pkg.customer_package_id)
                            setSelectedServiceId(pkg.services[0]?.service_id ?? null)
                            setSelectedFlexServiceIds([])
                            setLocalError('')
                          }}
                          className={cn(
                            'w-full rounded-xl border px-3 py-2.5 text-left transition-colors',
                            selected
                              ? 'border-primary/40 bg-primary/5 ring-1 ring-primary/20'
                              : 'border-border/50 bg-card hover:border-primary/20 hover:bg-muted/30',
                          )}
                        >
                          <p className="truncate text-sm font-semibold">{pkg.package_name}</p>
                          <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                            {posPackageTypeLabel(pkg.package_type)}
                          </p>
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            {isFlexibleComboPackage(pkg)
                              ? `${pkg.flexible_combo?.menu.length ?? 0} menu service(s) · ${pkg.flexible_combo?.picks_remaining ?? 0} pick(s) left`
                              : `${pkg.services.length} redeemable service(s)`}
                          </p>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className="flex min-h-0 flex-col p-4">
            {selectedPackage ? (
              isFlexibleCombo ? (
                <PosFlexibleComboPicker
                  pkg={selectedPackage}
                  sale={sale}
                  selectedServiceIds={selectedFlexServiceIds}
                  onChange={setSelectedFlexServiceIds}
                  disabled={submitting}
                />
              ) : (
                <>
                  <div className="rounded-xl border border-border/50 bg-muted/20 p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                      Choose covered service
                    </p>
                    <p className="mt-1 text-base font-semibold">{selectedPackage.package_name}</p>
                  </div>

                  <ul className="mt-3 space-y-1.5 overflow-y-auto">
                    {selectedPackage.services.map((service) => {
                      const selected = service.service_id === selectedServiceId

                      return (
                        <li key={service.service_id}>
                          <button
                            type="button"
                            disabled={submitting || !service.is_redeemable}
                            onClick={() => setSelectedServiceId(service.service_id)}
                            className={cn(
                              'w-full rounded-xl border px-3 py-2 text-left transition-colors',
                              selected
                                ? 'border-primary/40 bg-primary/5'
                                : 'border-border/50 hover:border-primary/20 hover:bg-muted/20',
                            )}
                          >
                            <p className="text-sm font-semibold">{service.service_name}</p>
                            <p className="text-[11px] text-muted-foreground">{service.remaining_label}</p>
                            <p className="mt-1 text-xs font-bold text-emerald-700">AED 0.00 — covered</p>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </>
              )
            ) : (
              <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-border/60 bg-muted/10 px-4 text-center text-[11px] text-muted-foreground">
                Select an active package to choose a covered service.
              </div>
            )}

            {displayError ? (
              <p className="mt-3 text-[11px] text-destructive">{displayError}</p>
            ) : null}

            <div className="mt-auto flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                disabled={submitting}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="flex-1"
                disabled={
                  submitting
                  || needsCustomer
                  || !selectedPackageId
                  || (isFlexibleCombo
                    ? selectedFlexServiceIds.length === 0
                    : !selectedServiceId || !selectedService?.is_redeemable)
                }
                onClick={handleRedeem}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding…
                  </>
                ) : isFlexibleCombo ? (
                  selectedFlexServiceIds.length > 1
                    ? `Add ${selectedFlexServiceIds.length} covered services`
                    : 'Add covered service'
                ) : (
                  'Add covered service'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
